import { useController, type FieldValues } from 'react-hook-form';
import { Dropdown, Form, type DropdownProps } from 'semantic-ui-react';

import { FieldError } from './field-error';
import type { FieldProps } from './field.types';

export type FieldDropdownProps<T extends FieldValues = FieldValues> =
  FieldProps<T, DropdownProps, 'value'> & {
    /**
     * Emit `null` instead of `''` when the selection is cleared, so
     * nullable schemas accept the cleared value.
     */
    nullable?: boolean;
  };

/**
 * Dropdown field wired to React Hook Form via `useController`.
 *
 * Wraps SUI `Dropdown` inside a `Form.Field` and handles the SUI `onChange`
 * unwrapping, error display, and `aria-errormessage` wiring automatically.
 *
 * Defaults to `selection`, `fluid`, and `selectOnBlur={false}` — all
 * overridable via props. Pass `multiple` for multi-select behaviour.
 *
 * All additional props are forwarded to the underlying SUI `Dropdown`.
 */
export const FieldDropdown = <T extends FieldValues>({
  className,
  control,
  label,
  multiple,
  name,
  nullable,
  required,
  width,
  ...rest
}: FieldDropdownProps<T>) => {
  const { field, fieldState } = useController({ control, name });
  const id = rest.id ?? name;
  const errorId = `${id}-error`;
  const hasError = !!fieldState.error;

  return (
    <Form.Field
      className={className}
      error={hasError}
      required={required}
      width={width}
    >
      {label && <label htmlFor={id}>{label}</label>}

      <Dropdown
        selectOnBlur={false}
        fluid
        selection
        {...rest}
        ref={field.ref}
        aria-errormessage={hasError ? errorId : undefined}
        aria-invalid={hasError || undefined}
        id={id}
        multiple={multiple}
        name={field.name}
        onBlur={field.onBlur}
        onChange={(_, { value }) =>
          field.onChange(nullable && value === '' ? null : value)
        }
        value={field.value ?? (multiple ? [] : '')}
      />

      <FieldError id={errorId} message={fieldState.error?.message} />
    </Form.Field>
  );
};
